'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { type SimilarListing, useSimilarListings } from '@/src/components/features/property/hooks/useSimilarListings';
import type { BedroomLabel, ComparisonRow, StaticCategory } from './comparisonTableUtils';
import { buildComparisonRows } from './comparisonTableUtils';

export type SimilarListingsFetchResult = {
  rows: Array<ComparisonRow>;
  bedroomLabels: Array<BedroomLabel>;
  hasCurrentListingColumn: boolean;
  staticCategories: Array<StaticCategory>;
  isLoading: boolean;
};

/**
 * Adapter that turns the hook output into comparison-table rows.
 *
 * The legacy port had an `isAllowedToRetain` source check + stale-race guard
 * tied to the Redux fetch; React Query's per-key cache makes most of that
 * unnecessary here.
 */
export const useSimilarListingsFetch = (
  currentListing: PropertyDetailListing | null | undefined,
): SimilarListingsFetchResult => {
  const t = useTranslations('hdp');
  const format = useFormatter();
  const maloneLotIdEncoded =
    (currentListing as { maloneLotIdEncoded?: string } | null | undefined)?.maloneLotIdEncoded ?? null;
  const unit = (currentListing as { unit?: string } | null | undefined)?.unit ?? null;
  const { data: similarListings, isLoading } = useSimilarListings({ maloneLotIdEncoded, unit });

  const { rows, bedroomLabels, hasCurrentListingColumn, staticCategories } = useMemo(() => {
    if (!currentListing || !similarListings || similarListings.length === 0) {
      return {
        rows: [] as Array<ComparisonRow>,
        bedroomLabels: [] as Array<BedroomLabel>,
        hasCurrentListingColumn: false,
        staticCategories: [] as Array<StaticCategory>,
      };
    }
    return buildComparisonRows(currentListing, similarListings as Array<SimilarListing>, {
      formatDistance: (miles) =>
        t('comparisonMilesAway', {
          count: miles,
          distance: format.number(miles, {
            minimumFractionDigits: miles === 1 ? 0 : 1,
            maximumFractionDigits: 1,
            useGrouping: false,
          }),
        }),
      availableLabel: t('comparisonAvailable'),
    });
  }, [currentListing, similarListings, t, format]);

  return { rows, bedroomLabels, hasCurrentListingColumn, staticCategories, isLoading };
};
